import { ModelConfig } from "api/models";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Mapping of display names to model configurations
 */
export type ModelTypeConfig = Record<string, ModelConfig>;

// ============================================================================
// Model Type Enums
// ============================================================================

/**
 * LLM Model Type Enum
 * Used as keys for LLM model configurations
 */
export enum LLM_MODEL_TYPE_KEYS {
  AZURE_OPENAI_GPT_4_1 = "Azure OpenAI GPT 4.1",
  AZURE_OPENAI_GPT_4_1_MINI = "Azure OpenAI GPT 4.1 Mini",
  OPENAI_GPT_4_1 = "OpenAI GPT 4.1",
  OPENAI_GPT_4_1_MINI = "OpenAI GPT 4.1 Mini",
  GPT_OSS_120B = "GPT OSS 120b",
  AGENTIQ_AI_1 = "AgentIQ AI 1",
}

/**
 * Embedding Model Type Enum
 * Used as keys for embedding model configurations
 */
export enum EMBEDDING_MODEL_TYPE_KEYS {
  AZURE_OPENAI_TEXT_EMBEDDING_3_SMALL = "Azure OpenAI Text Embedding 3 Small",
  BGE_EMBEDDING_M3 = "BGE Embedding M3",
  AGENTIQ_AI_EMBEDDING_1 = "AgentIQ AI Embedding 1",
}

// ============================================================================
// Type Aliases (after enum definitions)
// ============================================================================

/**
 * Type for LLM model type enum values
 */
export type LLMModelTypeValue = LLM_MODEL_TYPE_KEYS;

/**
 * Type for embedding model type enum values
 */
export type EmbeddingModelTypeValue = EMBEDDING_MODEL_TYPE_KEYS;

// ============================================================================
// LLM Model Type Configurations
// ============================================================================

/**
 * LLM Model Type Configurations
 * Maps display names (enum values) to their configuration templates
 */
export const LLM_MODEL_TYPE_CONFIGS: ModelTypeConfig = {
  [LLM_MODEL_TYPE_KEYS.AZURE_OPENAI_GPT_4_1]: {
    provider: "azure-openai",
    endpoint_url: "https://<resource>.openai.azure.com/",
    api_key: "AZURE_KEY",
    api_version: "2024-10-21",
    model_name: "gpt-4.1",
    model_kwargs: {}
  },
  [LLM_MODEL_TYPE_KEYS.AZURE_OPENAI_GPT_4_1_MINI]: {
    provider: "azure-openai",
    endpoint_url: "https://<resource>.openai.azure.com/",
    api_key: "AZURE_KEY",
    api_version: "2024-10-21",
    model_name: "gpt-4.1-mini",
    model_kwargs: {}
  },
  [LLM_MODEL_TYPE_KEYS.OPENAI_GPT_4_1]: {
    provider: "openai",
    endpoint_url: "https://api.openai.com/v1",
    api_key: "OPENAI_KEY",
    model_name: "gpt-4.1",
    model_kwargs: {}
  },
  [LLM_MODEL_TYPE_KEYS.OPENAI_GPT_4_1_MINI]: {
    provider: "openai",
    endpoint_url: "https://api.openai.com/v1",
    api_key: "OPENAI_KEY",
    model_name: "gpt-4.1-mini",
    model_kwargs: {}
  },
  [LLM_MODEL_TYPE_KEYS.GPT_OSS_120B]: {
    provider: "vllm",
    endpoint_url: "http://localhost:8000/v1",
    api_key: "not-null",
    model_name: "openai/gpt-oss-120b",
    model_kwargs: {}
  },
  [LLM_MODEL_TYPE_KEYS.AGENTIQ_AI_1]: {
    provider: "agentiqai",
    endpoint_url: "http://localhost:8000/v1",
    api_key: "not-null",
    model_name: "agentiqai-1",
  }
} as const;

// ============================================================================
// Embedding Model Type Configurations
// ============================================================================

/**
 * Embedding Model Type Configurations
 * Maps display names (enum values) to their configuration templates
 */
export const EMBEDDING_MODEL_TYPE_CONFIGS: ModelTypeConfig = {
  [EMBEDDING_MODEL_TYPE_KEYS.AZURE_OPENAI_TEXT_EMBEDDING_3_SMALL]: {
    provider: "azure-openai",
    endpoint_url: "https://<resource>.openai.azure.com/",
    api_key: "AZURE_KEY",
    api_version: "2024-10-21",
    model_name: "text-embedding-3-small",
    dimension: 1536
  },
  [EMBEDDING_MODEL_TYPE_KEYS.BGE_EMBEDDING_M3]: {
    provider: "vllm",
    endpoint_url: "http://localhost:8080/v1",
    api_key: "not-null",
    model_name: "BAAI/bge-m3",
    dimension: 1024,
    model_kwargs: {}
  },
  [EMBEDDING_MODEL_TYPE_KEYS.AGENTIQ_AI_EMBEDDING_1]: {
    provider: "agentiqai",
    endpoint_url: "http://localhost:8080/v1",
    api_key: "not-null",
    model_name: "agentiqai-embedding-1",
  },
} as const;

// ============================================================================
// Enum to Display Name Mappings
// ============================================================================

/**
 * Maps LLM enum values (from API) to display names (enum keys)
 */
export const LLM_ENUM_TO_DISPLAY: Readonly<Record<string, LLM_MODEL_TYPE_KEYS>> = {
  "GPT_OSS": LLM_MODEL_TYPE_KEYS.GPT_OSS_120B,
  "AZURE_GPT_41": LLM_MODEL_TYPE_KEYS.AZURE_OPENAI_GPT_4_1,
  "AZURE_GPT_41_MINI": LLM_MODEL_TYPE_KEYS.AZURE_OPENAI_GPT_4_1_MINI,
  "GPT_41": LLM_MODEL_TYPE_KEYS.OPENAI_GPT_4_1,
  "GPT_41_MINI": LLM_MODEL_TYPE_KEYS.OPENAI_GPT_4_1_MINI,
  "AGENTIQ_AI_1": LLM_MODEL_TYPE_KEYS.AGENTIQ_AI_1,
} as const;

/**
 * Maps embedding enum values (from API) to display names (enum keys)
 */
export const EMBEDDING_ENUM_TO_DISPLAY: Readonly<Record<string, EMBEDDING_MODEL_TYPE_KEYS>> = {
  "AZURE_TEXT": EMBEDDING_MODEL_TYPE_KEYS.AZURE_OPENAI_TEXT_EMBEDDING_3_SMALL,
  "BGE": EMBEDDING_MODEL_TYPE_KEYS.BGE_EMBEDDING_M3,
  "AGENTIQ_AI_EMBEDDING_1": EMBEDDING_MODEL_TYPE_KEYS.AGENTIQ_AI_EMBEDDING_1,
} as const;

// ============================================================================
// Model Name to Display Name Mappings (Auto-generated from configs)
// ============================================================================

/**
 * Generates a mapping from model_name to display name (enum value) from configs
 */
function generateModelNameMapping(configs: ModelTypeConfig): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const [displayName, config] of Object.entries(configs)) {
    if (config.model_name) {
      mapping[config.model_name] = displayName;
    }
  }
  return mapping;
}

/**
 * Maps LLM model names (from metadata.model_name) to display names (enum values)
 * Auto-generated from LLM_MODEL_TYPE_CONFIGS
 */
export const LLM_MODEL_NAME_TO_DISPLAY: Readonly<Record<string, LLM_MODEL_TYPE_KEYS>> = 
  Object.freeze(generateModelNameMapping(LLM_MODEL_TYPE_CONFIGS) as Record<string, LLM_MODEL_TYPE_KEYS>);

/**
 * Maps embedding model names (from metadata.model_name) to display names (enum values)
 * Auto-generated from EMBEDDING_MODEL_TYPE_CONFIGS
 */
export const EMBEDDING_MODEL_NAME_TO_DISPLAY: Readonly<Record<string, EMBEDDING_MODEL_TYPE_KEYS>> = 
  Object.freeze(generateModelNameMapping(EMBEDDING_MODEL_TYPE_CONFIGS) as Record<string, EMBEDDING_MODEL_TYPE_KEYS>);
